import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const BackupManagementPanel = ({ backups, onCreateBackup, onRestoreBackup, onDeleteBackup }) => {
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [backupType, setBackupType] = useState('full');

  const backupTypeOptions = [
    { value: 'full', label: 'نسخة احتياطية كاملة' },
    { value: 'database', label: 'قاعدة البيانات فقط' },
    { value: 'files', label: 'الملفات فقط' },
    { value: 'config', label: 'الإعدادات فقط' },
  ];

  const handleCreateBackup = () => {
    onCreateBackup(backupType);
  };

  const handleRestore = (backupId) => {
    if (window.confirm('هل أنت متأكد من استعادة هذه النسخة الاحتياطية؟ سيتم استبدال البيانات الحالية.')) {
      onRestoreBackup(backupId);
    }
  };

  const handleDelete = (backupId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه النسخة الاحتياطية؟ لا يمكن التراجع عن هذا الإجراء.')) {
      onDeleteBackup(backupId);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024))?.toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024))?.toFixed(2) + ' GB';
  };

  const backupStatusColors = {
    completed: 'bg-[var(--color-success)] text-white',
    inProgress: 'bg-[var(--color-warning)] text-white',
    failed: 'bg-[var(--color-error)] text-white',
  };

  const backupTypeIcons = {
    full: 'Database',
    database: 'Server',
    files: 'FolderOpen',
    config: 'Settings',
  };

  return (
    <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-warm">
      <div className="p-4 md:p-6 border-b border-[var(--color-border)]">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-[var(--color-foreground)]">
              إدارة النسخ الاحتياطية
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
              إنشاء واستعادة النسخ الاحتياطية للنظام
            </p>
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <Select
              options={backupTypeOptions}
              value={backupType}
              onChange={setBackupType}
              className="flex-1 lg:w-64"
            />
            <Button
              variant="default"
              iconName="Download"
              onClick={handleCreateBackup}
            >
              إنشاء نسخة احتياطية
            </Button>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-6">
        <div className="space-y-3">
          {backups?.map((backup) => (
            <div
              key={backup?.id}
              className="flex flex-col lg:flex-row items-start lg:items-center gap-4 p-4 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)] hover:shadow-warm transition-shadow"
            >
              <div className={`p-3 rounded-lg bg-[var(--color-muted)]`}>
                <Icon name={backupTypeIcons?.[backup?.type]} size={24} color="var(--color-primary)" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-[var(--color-foreground)]">{backup?.name}</h4>
                    <p className="text-xs text-[var(--color-muted-foreground)] mt-1">{backup?.description}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${backupStatusColors?.[backup?.status]}`}>
                    {backup?.statusLabel}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--color-muted-foreground)]">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    {backup?.createdAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="HardDrive" size={12} />
                    {formatFileSize(backup?.size)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={12} />
                    {backup?.createdBy}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full lg:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={() => window.open(backup?.downloadUrl, '_blank')}
                  className="flex-1 lg:flex-none"
                >
                  تحميل
                </Button>
                {backup?.status === 'completed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RotateCcw"
                    onClick={() => handleRestore(backup?.id)}
                    className="flex-1 lg:flex-none"
                  >
                    استعادة
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="Trash2"
                  onClick={() => handleDelete(backup?.id)}
                />
              </div>
            </div>
          ))}

          {backups?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Database" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-[var(--color-muted-foreground)] mb-4">لا توجد نسخ احتياطية متاحة</p>
              <Button
                variant="outline"
                iconName="Download"
                onClick={handleCreateBackup}
              >
                إنشاء أول نسخة احتياطية
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="p-4 md:p-6 border-t border-[var(--color-border)] bg-[var(--color-muted)]">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-[var(--color-foreground)] mb-2">
              معلومات مهمة حول النسخ الاحتياطية
            </h4>
            <ul className="text-xs text-[var(--color-muted-foreground)] space-y-1">
              <li>• يتم إنشاء نسخة احتياطية تلقائية يومياً في الساعة 2:00 صباحاً</li>
              <li>• يتم الاحتفاظ بالنسخ الاحتياطية لمدة 30 يوماً</li>
              <li>• النسخ الاحتياطية الكاملة تشمل قاعدة البيانات والملفات والإعدادات</li>
              <li>• يمكن تحميل النسخ الاحتياطية وتخزينها خارجياً للأمان الإضافي</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupManagementPanel;